/**
 * Approval Queue
 * Batched tool approval management
 */

import { tools } from './registry.js';

class ApprovalQueue {
  constructor() {
    this.queue = [];
    this.listeners = new Set();
    this.batchTimer = null;
    this.batchWindow = 2000; // 2 second batch window
    this.maxBatchSize = 10;
  }

  /**
   * Add an approval request to the queue
   */
  addRequest(request) {
    const item = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      status: 'pending', // pending, approved, denied
      ...request,
    };

    this.queue.push(item);
    this.notify();

    // Start batch timer if not running
    if (!this.batchTimer && this.queue.filter(q => q.status === 'pending').length <= this.maxBatchSize) {
      this.batchTimer = setTimeout(() => {
        this.batchTimer = null;
        this.notify();
      }, this.batchWindow);
    }

    return item.id;
  }

  /**
   * Approve a single request
   */
  async approve(id) {
    const item = this.queue.find(q => q.id === id);
    if (!item) return;

    item.status = 'approved';
    const tool = tools.get(item.toolName);
    if (tool) {
      item.result = await tool.execute(item.params);
    }
    this.notify();
    return item.result;
  }

  /**
   * Deny a single request
   */
  deny(id) {
    const item = this.queue.find(q => q.id === id);
    if (!item) return;
    item.status = 'denied';
    this.notify();
  }

  /**
   * Approve all pending requests
   */
  async approveAll() {
    const pending = this.queue.filter(q => q.status === 'pending');
    for (const item of pending) {
      await this.approve(item.id);
    }
  }

  /**
   * Deny all pending requests
   */
  denyAll() {
    this.queue
      .filter(q => q.status === 'pending')
      .forEach(q => { q.status = 'denied'; });
    this.notify();
  }

  /**
   * Get pending items
   */
  getPending() {
    return this.queue.filter(q => q.status === 'pending');
  }

  /**
   * Clear resolved items
   */
  clearResolved() {
    this.queue = this.queue.filter(q => q.status === 'pending');
    this.notify();
  }

  /**
   * Subscribe to queue changes
   */
  onUpdate(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notify() {
    const pending = this.getPending();
    this.listeners.forEach(cb => cb(pending, this.queue));
  }
}

export const approvals = new ApprovalQueue();
